/**
 * InfinitSales - Campos Personalizados (Custom Fields) Service
 *
 * Espelha o formulário em ABAS do Kommo. O schema (abas + definições) é do
 * workspace; os valores são por contato (mesmo payload nos 2 inboxes).
 */
import api from './api';
import { isOpaqueKey } from '../../utils/channels';
import requestCache from './requestCache';

// Chave do contato para a URL. Telefone vira so digitos; chave OPACA de
// Instagram/Messenger ('instagram:{igsid}' / 'messenger:{psid}') viaja
// intacta e codificada: apagar o prefixo aqui gravava tags, comentarios e
// campos numa chave indistinguivel de telefone (arquitetura empresarial,
// fase 1, mapa G). A canonizacao e sempre do servidor.
const sanitizePhone = (phone) => {
  if (!phone) return '';
  const raw = String(phone).trim();
  // O mapa de canais conhece os prefixos opacos (instagram:, messenger:,
  // widget:); um regex escrito a mao deixou o widget virar digitos (23/09).
  if (isOpaqueKey(raw)) return encodeURIComponent(raw);
  return raw.replace(/\D/g, '');
};

// Schema (abas + definicoes) e catalogo do workspace: os 4 inboxes, o drawer
// do contato, o filtro da lista e o painel de campos pediam o mesmo GET a cada
// troca de aba. Cache de 5 min, invalidado em toda mutacao deste service.
const SCHEMA_CACHE_KEY = 'customFields:schema';
const SCHEMA_TTL_MS = 5 * 60_000;

const customFieldsService = {
  // ---- Schema (abas + definições) ----
  async getSchema({ force = false } = {}) {
    return requestCache.cachedGet(
      SCHEMA_CACHE_KEY,
      async () => {
        const res = await api.get('/api/custom-fields/schema');
        return res.data?.data || { tabs: [], definitions: [] };
      },
      { ttl: SCHEMA_TTL_MS, force },
    );
  },

  // ---- Abas ----
  async createTab(name) {
    const res = await api.post('/api/custom-fields/tabs', { name });
    requestCache.invalidate(SCHEMA_CACHE_KEY);
    return res.data?.data;
  },

  async renameTab(tabId, name) {
    const res = await api.patch(`/api/custom-fields/tabs/${tabId}`, { name });
    requestCache.invalidate(SCHEMA_CACHE_KEY);
    return res.data?.data;
  },

  async reorderTabs(tabIds) {
    const res = await api.put('/api/custom-fields/tabs/order', { tab_ids: tabIds });
    requestCache.invalidate(SCHEMA_CACHE_KEY);
    return res.data?.data;
  },

  async deleteTab(tabId) {
    const res = await api.delete(`/api/custom-fields/tabs/${tabId}`);
    requestCache.invalidate(SCHEMA_CACHE_KEY);
    return res.data?.data;
  },

  // ---- Definições (campos) ----
  async createDefinition(definition) {
    const res = await api.post('/api/custom-fields/definitions', definition);
    requestCache.invalidate(SCHEMA_CACHE_KEY);
    return res.data?.data;
  },

  async updateDefinition(definitionId, patch) {
    const res = await api.patch(`/api/custom-fields/definitions/${definitionId}`, patch);
    requestCache.invalidate(SCHEMA_CACHE_KEY);
    return res.data?.data;
  },

  async reorderDefinitions(tabId, definitionIds) {
    const res = await api.put('/api/custom-fields/definitions/order', {
      tab_id: tabId,
      definition_ids: definitionIds,
    });
    requestCache.invalidate(SCHEMA_CACHE_KEY);
    return res.data?.data;
  },

  async deleteDefinition(definitionId) {
    const res = await api.delete(`/api/custom-fields/definitions/${definitionId}`);
    requestCache.invalidate(SCHEMA_CACHE_KEY);
    return res.data?.data;
  },

  // ---- Valores por contato ----
  async getContactFields(phone) {
    const res = await api.get(`/api/custom-fields/contacts/${sanitizePhone(phone)}`);
    return res.data?.data || { tabs: [], definitions: [], values: {} };
  },

  async setContactFields(phone, values) {
    const res = await api.put(`/api/custom-fields/contacts/${sanitizePhone(phone)}`, { values });
    // O chip "campos obrigatórios" (RequiredFieldsSignal) cacheia o /required
    // por 30s — sem invalidar aqui, o alerta persistia após preencher.
    requestCache.invalidate('requiredStatus:');
    return res.data?.data;
  },

  // Sinalização: obrigatórios pendentes na etapa/funil atual do contato.
  async getContactRequiredStatus(phone) {
    const res = await api.get(`/api/custom-fields/contacts/${sanitizePhone(phone)}/required`);
    return res.data?.data || { required: [], missing: [], required_count: 0, missing_count: 0 };
  },
};

export const FIELD_TYPE_LABELS = {
  text: 'Texto',
  textarea: 'Texto longo',
  number: 'Número',
  currency: 'Moeda (R$)',
  date: 'Data',
  datetime: 'Data e hora',
  select: 'Seleção única',
  multiselect: 'Múltipla escolha',
  checkbox: 'Sim/Não',
};

export const OPTION_FIELD_TYPES = ['select', 'multiselect'];

export default customFieldsService;
